import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Button, Card, CardContent, CardHeader, Spinner } from "@learn-me/ui";
import { ArrowLeft, FileText, Shield } from "lucide-react";
import toast from "react-hot-toast";
import { formatCurrency } from "@learn-me/utils";
import type { ApiResponse } from "@learn-me/types";
import { api } from "@/lib/api";
import { materialsApi } from "@/features/materials/api/materialsApi";
import TermsCheckbox from "@/components/legal/TermsCheckbox";
import {
  LEGAL_TERMS_VERSION,
  LEGAL_PRIVACY_VERSION,
} from "@/content/legal/versions";

const PAYPAL_CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID || "";

export default function MaterialCheckoutPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const { data: material, isLoading } = useQuery({
    queryKey: ["material-checkout", slug],
    queryFn: () => materialsApi.getBySlug(slug!),
    enabled: !!slug,
  });

  const createOrderMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<
        ApiResponse<{ orderId: string; approvalUrl: string }>
      >("/payments/paypal/create-order", {
        materialId: material!.id,
        termsAcceptance: {
          termsVersion: LEGAL_TERMS_VERSION,
          privacyVersion: LEGAL_PRIVACY_VERSION,
          contextPath: "/checkout/material",
        },
      });
      return response.data.data!;
    },
  });

  const captureOrderMutation = useMutation({
    mutationFn: async (orderId: string) => {
      const response = await api.post<ApiResponse<unknown>>(
        "/payments/paypal/capture-order",
        { orderId },
      );
      return response.data.data!;
    },
    onSuccess: () => {
      toast.success("¡Compra realizada con éxito!");
      navigate("/mis-materiales");
    },
    onError: () => {
      toast.error("Error al procesar el pago. Intenta de nuevo.");
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!material) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-2xl font-bold mb-2">Material no encontrado</h1>
        <Link to="/materiales">
          <Button>Ver materiales</Button>
        </Link>
      </div>
    );
  }

  const isProcessing =
    createOrderMutation.isPending || captureOrderMutation.isPending;
  const sale = Number(material.salePrice);

  return (
    <div className="py-8">
      <div className="container max-w-4xl">
        <Link
          to={`/materiales/${material.slug}`}
          className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver al material
        </Link>

        <div className="grid lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <h2 className="text-xl font-semibold">Resumen del Pedido</h2>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-6">
                <div className="w-24 h-16 bg-muted rounded flex items-center justify-center flex-shrink-0">
                  <FileText className="h-6 w-6 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium">{material.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    Material digital descargable
                  </p>
                </div>
              </div>
              <div className="border-t pt-4 flex justify-between font-bold text-lg">
                <span>Total</span>
                <span>{formatCurrency(sale, material.currency)}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-xl font-semibold">Método de Pago</h2>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <TermsCheckbox
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                />
              </div>
              {PAYPAL_CLIENT_ID ? (
                <PayPalScriptProvider
                  options={{
                    clientId: PAYPAL_CLIENT_ID,
                    currency: material.currency,
                  }}
                >
                  <PayPalButtons
                    style={{ layout: "vertical", shape: "rect" }}
                    disabled={!acceptedTerms || isProcessing}
                    createOrder={async () => {
                      if (!acceptedTerms) {
                        toast.error(
                          "Debes aceptar los Términos y la Política de Privacidad",
                        );
                        throw new Error("terms_not_accepted");
                      }
                      const result = await createOrderMutation.mutateAsync();
                      return result.orderId;
                    }}
                    onApprove={async (data) => {
                      await captureOrderMutation.mutateAsync(data.orderID);
                    }}
                    onError={(err) => {
                      console.error("PayPal error:", err);
                      toast.error("Error con PayPal. Intenta de nuevo.");
                    }}
                  />
                </PayPalScriptProvider>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>PayPal no está configurado.</p>
                </div>
              )}
              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Shield className="h-4 w-4" />
                <span>Pago seguro con PayPal</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
